import DarkThemeLoader from '../Loader/DarkThemeLoader';
import LightThemeLoader from '../Loader/LightThemeLoader';
import { ButtonMode } from './button-mode.enum';
import { ButtonProps } from './button-prop.interface';
import { ButtonSize } from './button-size.enum';
import { ButtonState } from './button-state.enum';
import { ButtonType } from './button-type.enum';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';

/**
 * Modern Button Component
 *
 * A beautifully designed button component with glassmorphism effects, soft gradients, and smooth animations.
 * Features modern design patterns with pastel colors and clean typography.
 */
const Button: React.FC<ButtonProps> = ({
  label,
  icon,
  onClick,
  state = ButtonState.Enabled,
  type = ButtonType.Filled,
  size = ButtonSize.Big,
  mode = ButtonMode.OnLight,
  iconOnly = false,
  iconBefore,
  iconAfter,
  className,
  href,
  target,
  rel,
  isLoading = false,
  animated = true,
  selected = false,
  interactive = true,
  htmlButtonElementType = undefined,
  children,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const isDisabled = state === ButtonState.Disabled || isLoading;

  /**
   * Get base styles that are common to all button variants
   */
  const getBaseStyles = () => {
    const baseStyles = [
      'inline-flex',
      'items-center',
      'justify-center',
      'font-spaceGrotesk',
      'font-medium',
      'transition-all',
      'duration-300',
      'ease-out',
      'relative',
      'overflow-hidden',
      'select-none',
      'outline-none',
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-offset-2',
    ];

    if (animated && !isDisabled) {
      baseStyles.push('transform-gpu');
    }

    return baseStyles;
  };

  /**
   * Get size-specific styles
   */
  const getSizeStyles = () => {
    if (iconOnly) {
      return size === ButtonSize.Medium ? ['w-9', 'h-9', 'p-2', 'rounded-2xl'] : ['w-11', 'h-11', 'p-3', 'rounded-2xl'];
    }

    return size === ButtonSize.Medium
      ? ['h-9', 'px-4', 'py-2', 'text-sm', 'rounded-2xl']
      : ['h-11', 'px-6', 'py-3', 'text-base', 'rounded-2xl'];
  };

  /**
   * Get type and state-specific styles with clean, minimal colors
   */
  const getTypeStyles = () => {
    if (type === ButtonType.Filled) {
      if (isDisabled) {
        return ['bg-gray-100', 'text-gray-400', 'cursor-not-allowed', 'border', 'border-gray-100'];
      }

      // Subtle gradient with improved contrast
      const baseStyles = [
        'bg-white',
        'text-gray-700',
        'shadow-md',
        'shadow-gray-200/40',
        'border',
        'border-gray-200',
        'backdrop-blur-sm',
        'focus:ring-gray-200/50',
      ];

      if (isHovered && interactive) {
        baseStyles.push('bg-gray-50', 'text-gray-800', 'shadow-lg', 'scale-105', '-translate-y-0.5');
      }

      if (isPressed && interactive) {
        baseStyles.push('bg-gray-100', 'text-gray-800', 'scale-100', 'translate-y-0', 'shadow-sm');
      }

      return baseStyles;
    }

    if (type === ButtonType.Outlined) {
      if (isDisabled) {
        return ['bg-transparent', 'text-gray-400', 'border', 'border-gray-200', 'cursor-not-allowed'];
      }

      const baseStyles = [
        'bg-white/70',
        'backdrop-blur-sm',
        'border',
        'border-gray-300',
        'text-gray-700',
        'shadow-sm',
        'focus:ring-gray-200/50',
      ];

      if (isHovered && interactive) {
        baseStyles.push('bg-white', 'border-gray-400', 'text-gray-800', 'shadow-md', 'scale-105', '-translate-y-0.5');
      }

      if (isPressed && interactive) {
        baseStyles.push('bg-white/90', 'border-gray-300', 'scale-100', 'translate-y-0', 'shadow-sm');
      }

      return baseStyles;
    }

    if (type === ButtonType.Ghost) {
      if (isDisabled) {
        return ['bg-transparent', 'text-gray-400', 'cursor-not-allowed'];
      }

      const baseStyles = ['bg-transparent', 'text-gray-700', 'focus:ring-gray-200/50'];

      if (isHovered && interactive) {
        baseStyles.push('bg-gray-50', 'text-gray-800', 'shadow-sm', 'scale-105', '-translate-y-0.5');
      }

      if (isPressed && interactive) {
        baseStyles.push('bg-gray-100', 'shadow-none', 'scale-100', 'translate-y-0');
      }

      return baseStyles;
    }

    if (type === ButtonType.Link) {
      if (isDisabled) {
        return ['bg-transparent', 'text-gray-400', 'cursor-not-allowed'];
      }

      const baseStyles = [
        'bg-transparent',
        'text-gray-600',
        'underline',
        'underline-offset-2',
        'decoration-1',
        'focus:ring-gray-200/50',
        'p-0',
        'h-auto',
      ];

      if (isHovered && interactive) {
        baseStyles.push('text-gray-700', 'decoration-2');
      }

      return baseStyles;
    }

    if (type === ButtonType.Error) {
      if (isDisabled) {
        return ['bg-red-100', 'text-red-400', 'cursor-not-allowed', 'border', 'border-red-100'];
      }

      const baseStyles = [
        'bg-gradient-to-br',
        'from-red-500',
        'to-rose-500',
        'text-white',
        'shadow-md',
        'shadow-red-300/40',
        'border',
        'border-red-400/30',
        'backdrop-blur-sm',
        'focus:ring-red-300/50',
      ];

      if (isHovered && interactive) {
        baseStyles.push('from-red-600', 'to-rose-600', 'shadow-lg', 'scale-105', '-translate-y-0.5');
      }

      if (isPressed && interactive) {
        baseStyles.push('from-red-700', 'to-rose-700', 'scale-100', 'translate-y-0', 'shadow-sm');
      }

      return baseStyles;
    }

    return [];
  };

  /**
   * Shared active treatment for toggle-like buttons such as category pills.
   */
  const getSelectedStyles = () => {
    if (!selected || isDisabled) {
      return [];
    }

    return [
      '!bg-content-brand-primary-enabled',
      '!text-white',
      'border',
      '!border-content-brand-primary-enabled',
      'shadow-[0_12px_26px_rgba(248,113,84,0.22)]',
    ];
  };

  /**
   * Handle mouse events for interactive states
   */
  const handleMouseEnter = useCallback(() => {
    if (!isDisabled && interactive) {
      setIsHovered(true);
    }
  }, [isDisabled, interactive]);

  const handleMouseLeave = useCallback(() => {
    if (!isDisabled && interactive) {
      setIsHovered(false);
      setIsPressed(false);
    }
  }, [isDisabled, interactive]);

  const handleMouseDown = useCallback(() => {
    if (!isDisabled && interactive) {
      setIsPressed(true);
    }
  }, [isDisabled, interactive]);

  const handleMouseUp = useCallback(() => {
    if (!isDisabled && interactive) {
      setIsPressed(false);
    }
  }, [isDisabled, interactive]);

  const handleClick = useCallback(() => {
    if (!isDisabled && onClick) {
      void onClick();
    }
  }, [isDisabled, onClick]);

  const buttonClasses = classNames(
    ...getBaseStyles(),
    ...getSizeStyles(),
    ...getTypeStyles(),
    ...getSelectedStyles(),
    className
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center gap-2">
          {mode === ButtonMode.OnLight && type !== ButtonType.Outlined ? (
            <LightThemeLoader noMargin={iconOnly} />
          ) : (
            <DarkThemeLoader noMargin={iconOnly} />
          )}
          {!iconOnly && label && <span className="leading-none">{typeof label === 'string' ? label : label}</span>}
          {!iconOnly && !label && children}
        </div>
      );
    }

    if (iconOnly && icon) {
      return <span className="flex items-center justify-center">{icon}</span>;
    }

    return (
      <div className="flex items-center gap-2">
        {iconBefore && <span className="flex items-center">{iconBefore}</span>}
        {label && <span className="leading-none">{typeof label === 'string' ? label : label}</span>}
        {!label && children}
        {iconAfter && <span className="flex items-center">{iconAfter}</span>}
      </div>
    );
  };

  const content = (
    <>
      {/* Soft shimmer effect for hover */}
      {!isDisabled && animated && (
        <div
          className={classNames(
            'absolute inset-0 opacity-0 bg-gradient-to-r from-transparent via-white/20 to-transparent',
            'transition-opacity duration-300 pointer-events-none',
            isHovered && 'opacity-100'
          )}
        />
      )}

      {/* Content */}
      <div className="relative z-10">{renderContent()}</div>
    </>
  );

  if (href) {
    return (
      <a
        href={isDisabled ? undefined : href}
        target={target}
        rel={rel}
        className={buttonClasses}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        aria-disabled={isDisabled || undefined}
        aria-pressed={selected || undefined}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type={htmlButtonElementType}
      className={buttonClasses}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      disabled={isDisabled}
      aria-pressed={selected || undefined}
    >
      {content}
    </button>
  );
};

export default Button;
